import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { useValidateFiatWallet } from '#src/components/fiatWallet/CreateFiatWallet/DocumentCheck/useValidateFiatWallet.ts';
import { HandleIdReviewError } from '#src/components/fiatWallet/CreateFiatWallet/HandleIdReviewError/index.tsx';
import { IdentityVerificationResult } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/IdentityVerificationResult/index.tsx';
import { HostedIdentityVerification } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/index.tsx';
import type { JONUM_FLOW } from '#src/components/fiatWallet/CreateFiatWallet/HostedIdentityVerification/returnUrl.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import type { FileWithDataURL } from '#src/components/form/UploadFile/useUploadFile.ts';
import {
  ProofOfIdStep,
  ProofOfIdentityForm,
} from '#src/components/kyc/ProofOfIdentityForm/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useUploadWithPresign } from '#src/hooks/useUploadWithPresign.ts';

import { ReviewInfo } from './ReviewInfo.tsx';
import { VerificationInProgress } from './VerificationInProgress.tsx';

enum KycStep {
  REVIEW_INFO = 'REVIEW_INFO',
  CHOOSE_DOCUMENT = 'CHOOSE_DOCUMENT',
  UPLOAD = 'UPLOAD',
  UNDER_REVIEW = 'UNDER_REVIEW',
  VERIFICATION_RESULT = 'VERIFICATION_RESULT',
  VERIFICATION_REFUSED = 'VERIFICATION_REFUSED',
}

const Body = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
  min-height: 100%;
  justify-content: flex-start;
`;

type Props = {
  onDone: () => void;
  renderVerificationInProgress?: (props: { onDone: () => void }) => ReactNode;
  hostedVerificationFlow?: typeof JONUM_FLOW;
};

export const KycFlow = ({
  onDone,
  renderVerificationInProgress,
  hostedVerificationFlow,
}: Props) => {
  const {
    flags: { mangopayHostedIdv = false },
  } = useFeatureFlags();
  const { fiatWalletAccountable } = useCurrentUserContext();
  const hadWalletAtMount = useState(() => !!fiatWalletAccountable)[0];
  const [step, setStep] = useState<KycStep>(
    hadWalletAtMount ? KycStep.CHOOSE_DOCUMENT : KycStep.REVIEW_INFO
  );
  const [frontPage, setFrontPage] = useState<FileWithDataURL>();
  const [backPage, setBackPage] = useState<FileWithDataURL>();
  const uploadWithPresign = useUploadWithPresign();
  const { validateFiatWallet } = useValidateFiatWallet();

  const onSubmitDocuments = async (
    onResult: (result: GraphQLResult) => void
  ) => {
    if (!frontPage) return;

    const presignPromises = [
      uploadWithPresign(frontPage.file),
      ...(backPage ? [uploadWithPresign(backPage.file)] : []),
    ];
    const presignedKeys = await Promise.all(presignPromises);

    if (presignedKeys.some(key => !key)) return;

    const result = await validateFiatWallet({
      frontPagePresignedKey: presignedKeys[0] as string,
      ...(presignedKeys.length > 1
        ? { backPagePresignedKey: presignedKeys[1] }
        : {}),
    });
    onResult(result);
  };

  const onProofOfIdStepChange = (proofStep: ProofOfIdStep) => {
    setStep(
      proofStep === ProofOfIdStep.CHOOSE_DOCUMENT
        ? KycStep.CHOOSE_DOCUMENT
        : KycStep.UPLOAD
    );
  };

  const onBack = () => {
    if (step === KycStep.UPLOAD) {
      setStep(KycStep.CHOOSE_DOCUMENT);
    } else if (hadWalletAtMount) {
      onDone();
    } else {
      setStep(KycStep.REVIEW_INFO);
    }
  };

  if (step === KycStep.REVIEW_INFO) {
    return (
      <Body>
        <ReviewInfo onNext={() => setStep(KycStep.CHOOSE_DOCUMENT)} />
      </Body>
    );
  }

  if (step === KycStep.VERIFICATION_RESULT) {
    return (
      <Body>
        <IdentityVerificationResult
          onDone={onDone}
          renderVerificationInProgress={renderVerificationInProgress}
          onRefused={() => setStep(KycStep.VERIFICATION_REFUSED)}
        />
      </Body>
    );
  }

  if (step === KycStep.VERIFICATION_REFUSED) {
    return (
      <Body>
        <HandleIdReviewError onResubmit={() => setStep(KycStep.REVIEW_INFO)} />
      </Body>
    );
  }

  if (step === KycStep.CHOOSE_DOCUMENT && mangopayHostedIdv) {
    return (
      <Body>
        <HostedIdentityVerification
          onBack={onBack}
          flow={hostedVerificationFlow}
          onVerificationSubmitted={() => setStep(KycStep.VERIFICATION_RESULT)}
        />
      </Body>
    );
  }

  if (step === KycStep.CHOOSE_DOCUMENT || step === KycStep.UPLOAD) {
    return (
      <Body>
        <ProofOfIdentityForm
          frontPage={frontPage}
          setFrontPage={setFrontPage}
          backPage={backPage}
          setBackPage={setBackPage}
          onBack={onBack}
          onStepChange={onProofOfIdStepChange}
          onSubmit={onSubmitDocuments}
          onSuccess={() => setStep(KycStep.UNDER_REVIEW)}
          cotrolledUploadProofOfIdStep={
            step === KycStep.CHOOSE_DOCUMENT
              ? ProofOfIdStep.CHOOSE_DOCUMENT
              : ProofOfIdStep.UPLOAD
          }
        />
      </Body>
    );
  }

  if (renderVerificationInProgress) {
    return <>{renderVerificationInProgress({ onDone })}</>;
  }

  return (
    <Body>
      <VerificationInProgress onDone={onDone} />
    </Body>
  );
};
